import { useState, useEffect, useCallback, useMemo } from "react";
import { fetchReleases, type Release, ApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReleaseCard } from "@/components/ReleaseCard";

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; releases: Release[] };

/**
 * Changelog page. Bundle rows are the primary entry — each bundle row
 * carries a disclosure for the per-component rows that rolled up to it.
 * Per-component rows with no matching bundle (e.g. a standalone hotfix)
 * render as their own card at the bottom of the list.
 */
export function Releases() {
  const { status: authStatus } = useAuth();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [channelFilter, setChannelFilter] = useState<
    "all" | "stable" | "beta"
  >("all");

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const data = await fetchReleases();
      setState({ status: "loaded", releases: data.releases });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `Server error (${err.status})`
          : "Failed to load releases. Check your connection and try again.";
      setState({ status: "error", message });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (state.status !== "loaded") return [];
    return channelFilter === "all"
      ? state.releases
      : state.releases.filter((r) => r.channel === channelFilter);
  }, [state, channelFilter]);

  const groups = useMemo(() => groupBundlesWithComponents(filtered), [filtered]);

  if (state.status === "loading") {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="space-y-4">
          <div className="skeleton h-8 w-40 rounded-lg" />
          <div className="skeleton h-4 w-64 rounded" />
          <div className="space-y-3 mt-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton h-16 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-zinc-100 mb-2">
            Could not load releases
          </h2>
          <p className="text-sm text-zinc-400 mb-6">{state.message}</p>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const { releases } = state;
  const stableCount = releases.filter((r) => r.channel === "stable").length;
  const betaCount = releases.filter((r) => r.channel === "beta").length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">
          Releases
        </h1>
        <p className="text-zinc-400">
          {authStatus === "authenticated"
            ? "Every Blazeboard bundle, newest first. Expand a bundle to see the pinned component versions."
            : "Every Blazeboard bundle, newest first. Sign in to see which bundle your devices are running."}
        </p>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => setChannelFilter("all")}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            channelFilter === "all"
              ? "bg-zinc-800 text-zinc-100"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          All ({releases.length})
        </button>
        <button
          onClick={() => setChannelFilter("stable")}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            channelFilter === "stable"
              ? "bg-zinc-800 text-zinc-100"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Stable ({stableCount})
        </button>
        <button
          onClick={() => setChannelFilter("beta")}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            channelFilter === "beta"
              ? "bg-zinc-800 text-zinc-100"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Beta ({betaCount})
        </button>
      </div>

      {groups.bundles.length === 0 && groups.orphans.length === 0 ? (
        <EmptyState channelFilter={channelFilter} />
      ) : (
        <div className="space-y-4">
          {groups.bundles.map((entry) => (
            <BundleGroup
              key={`${entry.bundle.channel}:${entry.bundle.version}`}
              bundle={entry.bundle}
              components={entry.components}
            />
          ))}
          {groups.orphans.length > 0 && (
            <div className="space-y-3 pt-2">
              {groups.bundles.length > 0 && (
                <h2 className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Standalone component releases
                </h2>
              )}
              {groups.orphans.map((release) => (
                <ReleaseCard
                  key={`${release.channel}:${release.component ?? ""}:${release.version}`}
                  release={release}
                  variant="component"
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ channelFilter }: { channelFilter: string }) {
  return (
    <div className="bg-zinc-900/50 border border-dashed border-zinc-800 rounded-xl p-8 text-center">
      <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
        <svg
          className="w-6 h-6 text-zinc-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
          />
        </svg>
      </div>
      <h3 className="font-medium text-zinc-300 mb-1">
        No {channelFilter !== "all" ? channelFilter : ""} releases found
      </h3>
      <p className="text-sm text-zinc-500">
        {channelFilter !== "all"
          ? "Try switching to a different channel filter."
          : "No releases have been published yet."}
      </p>
    </div>
  );
}

interface BundleGroupProps {
  bundle: Release;
  components: Release[];
}

function BundleGroup({ bundle, components }: BundleGroupProps) {
  const [showComponents, setShowComponents] = useState(false);
  return (
    <div className="space-y-2">
      <ReleaseCard release={bundle} variant="bundle" />
      {components.length > 0 && (
        <div className="ml-0 sm:ml-4">
          <button
            onClick={() => setShowComponents(!showComponents)}
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors inline-flex items-center gap-1"
          >
            <svg
              className={`w-3 h-3 transition-transform ${showComponents ? "rotate-90" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            {showComponents ? "Hide" : "Show"} component releases (
            {components.length})
          </button>
          {showComponents && (
            <div className="mt-2 space-y-2">
              {components.map((c) => (
                <ReleaseCard
                  key={`${c.channel}:${c.component ?? ""}:${c.version}`}
                  release={c}
                  variant="component"
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Pair each bundle row with its pinned component-version rows (pulled
 * from `bundle.componentVersions`). The worker aggregates per-component
 * manifests into a single card per `(channel, version)` with multiple
 * artifacts, so we match by the artifact `name` (which carries the
 * component identity) rather than the top-level `component` field
 * (which is `null` on aggregated rows).
 *
 * Per-component rows that aren't referenced by any bundle drop into
 * `orphans` and render at the end as "standalone component releases".
 */
function groupBundlesWithComponents(releases: Release[]): {
  bundles: Array<{ bundle: Release; components: Release[] }>;
  orphans: Release[];
} {
  const bundleRows = releases.filter((r) => r.component === "bundle");
  const componentRows = releases.filter((r) => r.component !== "bundle");

  // Index by `${channel}:${version}` — one aggregated row per
  // (channel, version) pair. The per-component identity lives in
  // `artifacts[].name`.
  const componentByChannelVersion = new Map<string, Release>();
  for (const c of componentRows) {
    componentByChannelVersion.set(`${c.channel}:${c.version}`, c);
  }

  const claimedKeys = new Set<string>();
  const bundles = bundleRows.map((bundle) => {
    const matched = new Set<Release>();
    const versions = bundle.componentVersions ?? {};
    for (const [name, ver] of Object.entries(versions)) {
      if (!ver) continue;
      // Bundles sometimes pin a component version that was cut on the
      // opposite channel (beta bundle promoting a stable orchestrator).
      // Check the bundle's channel first, then fall back to the other
      // channel so the component still surfaces under its bundle.
      const primaryKey = `${bundle.channel}:${ver}`;
      const altChannel = bundle.channel === "stable" ? "beta" : "stable";
      const altKey = `${altChannel}:${ver}`;
      const candidate =
        componentByChannelVersion.get(primaryKey) ??
        componentByChannelVersion.get(altKey);
      if (
        candidate &&
        candidate.artifacts.some((a) => a.name === name)
      ) {
        matched.add(candidate);
        claimedKeys.add(`${candidate.channel}:${candidate.version}`);
      }
    }
    return { bundle, components: Array.from(matched) };
  });

  const orphans = componentRows.filter(
    (c) => !claimedKeys.has(`${c.channel}:${c.version}`),
  );

  return { bundles, orphans };
}
